import React, { useState, useEffect } from 'react';
import { useProfileModal } from '../../context/ProfileModalContext';
import { Meeting, MeetingMemberPreferences, NameListMode } from '../../services/meetingService';
import { formatWorkshopStartSchedule } from '../../services/workshop2026Service';
import styles from '../../styles/workshop2026Page.module.css';

interface WorkshopWorkshopPreferencesProps {
  meeting: Meeting | null;
  preferences: MeetingMemberPreferences;
  profilePhone?: string;
  userEmail?: string;
  onSave: (updates: Partial<MeetingMemberPreferences>) => Promise<void>;
  onSavePhone: (phone: string) => Promise<void>;
  embedded?: boolean;
}

const WorkshopWorkshopPreferences: React.FC<WorkshopWorkshopPreferencesProps> = ({
  meeting,
  preferences,
  profilePhone,
  userEmail,
  onSave,
  onSavePhone,
  embedded = false,
}) => {
  const { openProfileModal } = useProfileModal();
  const [nameListMode, setNameListMode] = useState<NameListMode>(preferences.name_list_mode);
  const [includePhoneOnList, setIncludePhoneOnList] = useState(preferences.include_on_printed_phone_list);
  const [includeEmailOnList, setIncludeEmailOnList] = useState(preferences.include_email_on_list);
  const [sponsorWillingness, setSponsorWillingness] = useState(preferences.sponsor_willingness);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState(profilePhone || '');
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [phoneSaved, setPhoneSaved] = useState(false);

  const hasPhone = !!profilePhone?.trim();
  const accountEmail = userEmail?.trim() || '';
  const startSchedule = formatWorkshopStartSchedule(meeting);

  useEffect(() => {
    setNameListMode(preferences.name_list_mode);
    setIncludePhoneOnList(preferences.include_on_printed_phone_list);
    setIncludeEmailOnList(preferences.include_email_on_list);
    setSponsorWillingness(preferences.sponsor_willingness);
  }, [preferences]);

  useEffect(() => {
    setPhoneDraft(profilePhone || '');
    if (profilePhone?.trim()) setEditingPhone(false);
  }, [profilePhone]);

  const persist = async (updates: Partial<MeetingMemberPreferences>) => {
    setSaving(true);
    setSaved(false);
    try {
      await onSave(updates);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  const handleSavePhone = async () => {
    const trimmed = phoneDraft.trim();
    if (!trimmed) return;
    setPhoneSaving(true);
    setPhoneSaved(false);
    try {
      await onSavePhone(trimmed);
      setEditingPhone(false);
      setPhoneSaved(true);
      setTimeout(() => setPhoneSaved(false), 2500);
    } finally {
      setPhoneSaving(false);
    }
  };

  const formContent = (
    <div className={styles.prefFormStack}>
      <div className={styles.giveToGetCallout}>
        <p className={styles.giveToGetTitle}>Sharing goes both ways — &ldquo;give to get&rdquo;</p>
        <p className={styles.giveToGetIntro}>You&apos;ll only see what you&apos;re willing to share:</p>
        <ul className={styles.giveToGetList}>
          <li>See members&apos; <strong>names</strong> only if you show your name</li>
          <li>See members&apos; <strong>phone numbers</strong> only if you list your phone</li>
          <li>See members&apos; <strong>email addresses</strong> only if you list your email</li>
        </ul>
        <p className={styles.giveToGetNote}>Your email is your account email — nothing to type in.</p>
      </div>

      <div className={styles.prefGroup}>
        <h4 className={styles.prefGroupTitle}>My name on the member list</h4>
        <p className={styles.prefGroupDesc}>
          You&apos;ll see other members&apos; names only if you show yours.
        </p>
        <div className={styles.prefChoiceStack}>
          <label className={styles.prefChoiceOption}>
            <input
              type="radio"
              name="nameList"
              checked={nameListMode === 'hidden'}
              onChange={() => {
                setNameListMode('hidden');
                persist({ name_list_mode: 'hidden' });
              }}
              disabled={saving}
            />
            <span>Don&apos;t ever show my name</span>
          </label>
          <label className={styles.prefChoiceOption}>
            <input
              type="radio"
              name="nameList"
              checked={nameListMode === 'when_workshop_starts'}
              onChange={() => {
                setNameListMode('when_workshop_starts');
                persist({ name_list_mode: 'when_workshop_starts' });
              }}
              disabled={saving}
            />
            <span>Show my name when the workshop starts ({startSchedule})</span>
          </label>
          <label className={styles.prefChoiceOption}>
            <input
              type="radio"
              name="nameList"
              checked={nameListMode === 'now'}
              onChange={() => {
                setNameListMode('now');
                persist({ name_list_mode: 'now' });
              }}
              disabled={saving}
            />
            <span>Show my name now (visible to other confirmed members)</span>
          </label>
        </div>
      </div>

      <div className={styles.prefGroup}>
        <h4 className={styles.prefGroupTitle}>Workshop phone list</h4>
        <p className={styles.prefGroupDesc}>
          Add your number to see other members&apos; numbers. The shared list goes live on the first
          session ({startSchedule}) and stays up for the rest of the workshop.
        </p>
        <div className={styles.prefChoiceStack}>
          <label className={styles.prefChoiceOption}>
            <input
              type="radio"
              name="phoneList"
              checked={includePhoneOnList}
              onChange={() => {
                setIncludePhoneOnList(true);
                persist({ include_on_printed_phone_list: true });
              }}
              disabled={saving}
            />
            <span>Yes, list my number (and let me see others&apos;)</span>
          </label>
          <label className={styles.prefChoiceOption}>
            <input
              type="radio"
              name="phoneList"
              checked={!includePhoneOnList}
              onChange={() => {
                setIncludePhoneOnList(false);
                setEditingPhone(false);
                persist({ include_on_printed_phone_list: false });
              }}
              disabled={saving}
            />
            <span>No — I won&apos;t see other members&apos; phone numbers</span>
          </label>
        </div>

        {includePhoneOnList && (
          <div className={styles.prefSubBlock}>
            {hasPhone && !editingPhone ? (
              <>
                <p className={styles.prefSubValue}>
                  Saved number: <strong>{profilePhone}</strong>
                </p>
                <div className={styles.prefActionRow}>
                  <button
                    type="button"
                    className={styles.textButton}
                    onClick={() => {
                      setPhoneDraft(profilePhone || '');
                      setEditingPhone(true);
                    }}
                  >
                    Update my number
                  </button>
                  <button type="button" className={styles.textButton} onClick={() => openProfileModal()}>
                    View my profile
                  </button>
                </div>
              </>
            ) : (
              <>
                <label className={styles.prefSubLabel} htmlFor="workshop-phone">
                  Phone number
                </label>
                <p className={styles.prefSubHint}>
                  {hasPhone
                    ? 'Updates your Recovery Commons profile for all meetings.'
                    : 'Saved to your Recovery Commons profile and used when the list goes live.'}
                </p>
                <div className={styles.prefPhoneRow}>
                  <input
                    id="workshop-phone"
                    type="tel"
                    className={styles.prefPhoneInput}
                    value={phoneDraft}
                    onChange={(e) => setPhoneDraft(e.target.value)}
                    placeholder="e.g. (555) 123-4567"
                    autoComplete="tel"
                    disabled={phoneSaving}
                  />
                  <button
                    type="button"
                    className={styles.prefPhoneSaveBtn}
                    onClick={handleSavePhone}
                    disabled={phoneSaving || !phoneDraft.trim()}
                  >
                    {phoneSaving ? 'Saving…' : 'Save number'}
                  </button>
                </div>
                <div className={styles.prefActionRow}>
                  {hasPhone && editingPhone && (
                    <button
                      type="button"
                      className={styles.textButton}
                      onClick={() => {
                        setPhoneDraft(profilePhone || '');
                        setEditingPhone(false);
                      }}
                    >
                      Cancel
                    </button>
                  )}
                  <button type="button" className={styles.textButton} onClick={() => openProfileModal()}>
                    View my profile
                  </button>
                </div>
                {phoneSaved && <p className={styles.prefStatusSaved}>Phone number saved.</p>}
              </>
            )}
          </div>
        )}
      </div>

      <div className={styles.prefGroup}>
        <h4 className={styles.prefGroupTitle}>Workshop email list</h4>
        <p className={styles.prefGroupDesc}>
          This shares your account email{accountEmail ? ` (${accountEmail})` : ''} with confirmed
          members. Same timing as the phone list.
        </p>
        <div className={styles.prefChoiceStack}>
          <label className={styles.prefChoiceOption}>
            <input
              type="radio"
              name="emailList"
              checked={includeEmailOnList}
              onChange={() => {
                setIncludeEmailOnList(true);
                persist({ include_email_on_list: true });
              }}
              disabled={saving}
            />
            <span>Yes, share my account email (and let me see others&apos;)</span>
          </label>
          <label className={styles.prefChoiceOption}>
            <input
              type="radio"
              name="emailList"
              checked={!includeEmailOnList}
              onChange={() => {
                setIncludeEmailOnList(false);
                persist({ include_email_on_list: false });
              }}
              disabled={saving}
            />
            <span>No — I won&apos;t see other members&apos; email addresses</span>
          </label>
        </div>

        {includeEmailOnList && accountEmail && (
          <div className={styles.prefSubBlock}>
            <p className={styles.prefSubValue}>
              Shared from your account: <strong>{accountEmail}</strong>
            </p>
            <p className={styles.prefSubHint}>
              This is your login email — to change it, update your account.
            </p>
            <div className={styles.prefActionRow}>
              <button type="button" className={styles.textButton} onClick={() => openProfileModal()}>
                View my profile
              </button>
            </div>
          </div>
        )}
      </div>

      <div className={styles.prefGroup}>
        <h4 className={styles.prefGroupTitle}>Available to sponsor</h4>
        <p className={styles.prefGroupDesc}>
          Willing to be listed as an available sponsor on the workshop phone list?
        </p>
        <div className={styles.prefChoiceStack}>
          <label className={styles.prefChoiceOption}>
            <input
              type="radio"
              name="sponsorWilling"
              checked={sponsorWillingness === 'yes'}
              onChange={() => {
                setSponsorWillingness('yes');
                persist({ sponsor_willingness: 'yes' });
              }}
              disabled={saving}
            />
            <span>Yes</span>
          </label>
          <label className={styles.prefChoiceOption}>
            <input
              type="radio"
              name="sponsorWilling"
              checked={sponsorWillingness === 'temporary'}
              onChange={() => {
                setSponsorWillingness('temporary');
                persist({ sponsor_willingness: 'temporary' });
              }}
              disabled={saving}
            />
            <span>Yes, temporary</span>
          </label>
          <label className={styles.prefChoiceOption}>
            <input
              type="radio"
              name="sponsorWilling"
              checked={sponsorWillingness === 'no'}
              onChange={() => {
                setSponsorWillingness('no');
                persist({ sponsor_willingness: 'no' });
              }}
              disabled={saving}
            />
            <span>No</span>
          </label>
        </div>
      </div>

      {saving && <p className={styles.prefStatus}>Saving…</p>}
      {!saving && saved && <p className={styles.prefStatusSaved}>Preferences saved.</p>}
    </div>
  );

  if (embedded) {
    return <div className={styles.preferencesEmbedded}>{formContent}</div>;
  }

  return (
    <section id="workshop-preferences" className={styles.preferencesSection}>
      <h3 className={styles.sectionSubheading}>My preferences for this workshop</h3>
      <p className={styles.helperText}>You can update these anytime. Only workshop members see this information.</p>
      {formContent}
    </section>
  );
};

export default WorkshopWorkshopPreferences;
